import React, { useCallback, useEffect, useState } from "react";
import uuid from "react-uuid";

import Vimeo from "@u-wave/react-vimeo";

import { getVideoDetails } from "services";

import { useAppType, useCancelAsyncOnUnmount, useInView } from "utils";

import { LazyLoadImage } from "components";

const id = uuid();

const getPlaceholderImage = (appType, placeholderImages) => {
  if (!placeholderImages) {
    return null;
  }
  if (appType.desktop && placeholderImages.desktop) {
    return placeholderImages.desktop;
  }
  if (placeholderImages.mobile) {
    return placeholderImages.mobile;
  }
  if (placeholderImages.link) {
    return placeholderImages;
  }
  if (placeholderImages) {
    return {
      link: placeholderImages,
    };
  }
  return null;
};

export const VideoPlayer = ({
  autoplay = false,
  disableDataLoad,
  disableResponsive,
  ignoreIntersectionObserver,
  loop,
  placeholderImages: placeholderImagesFromProps,
  vimeoId,
  dataTestId,
}) => {
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [videoInProgress, setVideoInProgress] = useState(false);
  const [placeholderImages, setPlaceholderImages] = useState(placeholderImagesFromProps || null);
  const signal = useCancelAsyncOnUnmount()();
  const appType = useAppType();

  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
    trackVisibility: true,
    delay: 100,
  });

  const hardcodedHeight = () => {
    if (appType.desktop) {
      return 340;
    }
    if (appType.mobile) {
      return 200;
    }
    if (appType.smallDesktop) {
      return 200;
    }
    return 340;
  };

  const loadVideoDetails = useCallback(() => {
    getVideoDetails({
      vimeoId,
      signal,
    }).then((res) => {
      setPlaceholderImages(res.placeholderImages);
      setLoading(false);
    });
  }, [signal, vimeoId]);

  useEffect(() => {
    if (!disableDataLoad && vimeoId) {
      loadVideoDetails();
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const placeHolderImage = getPlaceholderImage(appType, placeholderImages);
  const fallbackImage = "/images/misc/play-image.svg";

  return (
    <div className="vimeo-container" data-testid={dataTestId} id={id} height={hardcodedHeight()} ref={ref}>
      <>
        {/* Fallback image for all the videos except the Homepage top one */}
        {(loading || !isVideoReady) && !autoplay && (
          <div className="skeleton-background">
            <LazyLoadImage src={fallbackImage} width="100px" height={hardcodedHeight()} />
          </div>
        )}

        {/* Fallback image for homepage top video */}
        {(loading || !isVideoReady || !videoInProgress) && autoplay && (
          <div className="skeleton-background">
            <LazyLoadImage src={fallbackImage} width="100px" height="100px" />
          </div>
        )}

        {/* Showing a placeholder image until the video starts */}
        {!videoInProgress && placeHolderImage && !autoplay && (
          <div>
            <LazyLoadImage className="video-placeholder" src={placeHolderImage} width="100%" height="100%" />
          </div>
        )}

        {(inView || ignoreIntersectionObserver) && (
          <Vimeo
            autoplay={autoplay}
            className={`vimeo-player ${!isVideoReady ? "hidden" : ""}`}
            playsInline
            showByline={false}
            showPortrait={false}
            showTitle={false}
            video={vimeoId}
            background={autoplay}
            loop={loop}
            responsive={!disableResponsive}
            onReady={() => setIsVideoReady(true)}
            onProgress={() => setVideoInProgress(true)}
          />
        )}
      </>
      <div className="funky-shape-cut-out" />
    </div>
  );
};
